/**
 * SI-16: filter panel for the supplier-side Supplier Performance page.
 *
 * A supplier reports on itself, so the customer page's supplier-side filters
 * (division, supplier group/code/site, strategic, commodity, supplier type,
 * countries) have nothing to act on. What is left mirrors the legacy panel a
 * supplier saw: Customer group, Customer site, time period, rating band and the
 * N/A toggle. Options come from the rows already loaded — no extra API calls.
 *
 * Reuses `SupplierPerformanceFilterValues` so the hook keeps ONE filter state:
 * `division` = customer group, `site` = customer site, as on the customer page.
 *
 * CR-6403 rework 2: the same card, section shell and footer as the customer
 * panel and the dashboard's supplier view — Date Range · Customer · Supplier ·
 * Classification, each under the app-wide `FilterSection` heading. Behaviour
 * is deliberately NOT unified with the customer panel: the pickers here stay
 * instant because they filter client-side, and only the dates wait for Apply.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertCircle, Building2, CalendarRange, Download, Filter, RefreshCw, Search, Tags, Truck, X,
} from 'lucide-react';
import { FilterSection, FilterGrid, FilterField } from '../filters/FilterSection';
import { getLast12MonthsRange } from '../dashboard/dashboardFilterUtils';
import { SupplierPerformanceFilterValues } from './SupplierPerformanceFilters';
import { DEFAULT_FILTERS as DEFAULT_FILTER_VALUES } from '../../pages/supplier-performance/constants';

/** Horizontal divider with padding so it doesn't touch card edges. */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

export interface SupplierSelfPerformanceFilterOptions {
  groups: string[];
  sites: Array<{ id: number; name: string; group: string | null }>;
  grades: string[];
  /** Q8: the supplier's own sites present in the rows (>1 only at GROUP view). */
  supplierSites: Array<{ id: number; name: string }>;
}

interface SupplierSelfPerformanceFiltersProps {
  filters: SupplierPerformanceFilterValues;
  options: SupplierSelfPerformanceFilterOptions;
  onApplyFilters: (filters: SupplierPerformanceFilterValues) => void;
  onRefresh: () => void;
  onExport: () => void;
  /** CR-7297: the page's one Export control — busy, unavailable, and what it writes. */
  exporting?: boolean;
  exportDisabled?: boolean;
  exportTitle?: string;
}

export default function SupplierSelfPerformanceFilters({
  filters,
  options,
  onApplyFilters,
  onRefresh,
  onExport,
  exporting = false,
  exportDisabled = false,
  exportTitle,
}: SupplierSelfPerformanceFiltersProps) {
  const [local, setLocal] = useState<SupplierPerformanceFilterValues>(filters);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setLocal(filters); }, [filters]);

  // Pickers apply on change (they are client-side); the date inputs apply on
  // Search because they refetch from the server.
  const applyNow = <K extends keyof SupplierPerformanceFilterValues>(key: K, value: SupplierPerformanceFilterValues[K]) => {
    const next = { ...local, [key]: value };
    setLocal(next);
    onApplyFilters(next);
  };

  const last12 = getLast12MonthsRange();
  const isLast12 = local.dateFrom === last12.dateFrom && local.dateTo === last12.dateTo;

  const sitesInGroup = local.division === 'all'
    ? options.sites
    : options.sites.filter((s) => (s.group ?? '') === local.division);

  // Counted from the APPLIED filters, not the pending ones: the count
  // describes the results on screen, while "Not applied yet" describes the
  // edit that has not reached them. Counting `local` here made the bar say
  // "1 filter active" and "Not applied yet" at once, over an unfiltered table.
  //
  // CR-6403 rework 2: the date range counts only when it differs from the
  // default 12-month one, as on the customer panel (CR-7264) — the page opens
  // on that range, so counting any non-empty date made a fresh, unfiltered
  // page report "1" on the Filters badge.
  const activeCount = [
    filters.supplierSite !== 'all',
    filters.division !== 'all',
    filters.site !== 'all',
    filters.rating !== 'all',
    filters.dateFrom !== DEFAULT_FILTER_VALUES.dateFrom || filters.dateTo !== DEFAULT_FILTER_VALUES.dateTo,
    !filters.excludeNARatings,
  ].filter(Boolean).length;

  /**
   * A change is waiting on Apply.
   *
   * Only the date inputs can raise this. Every picker here filters the rows
   * already in the browser (the server returns the whole set for a supplier —
   * see `filteredSuppliers` in useSupplierPerformance), so they apply on
   * change and settle this back to false in the same tick; the dates refetch,
   * so they wait. The prompt therefore appears exactly when something is
   * genuinely unapplied, and never nags about a picker that already took.
   */
  const hasPendingChanges = useMemo(
    () => (Object.keys(local) as Array<keyof SupplierPerformanceFilterValues>)
      .some((key) => local[key] !== filters[key]),
    [local, filters],
  );

  const rangeSummary = isLast12
    ? 'Last 12 Months (Default)'
    : (local.dateFrom && local.dateTo ? `${local.dateFrom} to ${local.dateTo}` : 'all dates');

  const clearFilters = () => {
    setLocal(DEFAULT_FILTER_VALUES);
    onApplyFilters(DEFAULT_FILTER_VALUES);
  };

  return (
    <Card className="mb-4">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-xl">Filter Supplier Performance</CardTitle>
            {hasPendingChanges && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 transition-colors"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Changes not applied
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {/* CR-7297: the page's only Export (the toggle-side duplicate is gone). */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onExport}
              disabled={exporting || exportDisabled}
              aria-busy={exporting}
              title={exportTitle}
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                expanded
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeCount > 0 && (
                <span
                  data-testid="active-filter-count"
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    expanded
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-primary text-primary-foreground'
                  }`}
                >
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-0">
          {/* CR-6403 rework 3: inset section rules, matching the customer-side
              panel and the Purchasing / Quality panels. `[&>section]:border-t-0`
              cancels FilterSection's own full-bleed line so the SectionDividers
              are the only separators. */}
          <div className="bg-card [&>section]:border-t-0">
            {/* Date Range — the quick range applies on click, as the presets
                do on the dashboard and on the customer-side panel. */}
            <SectionDivider />
            <FilterSection title="Date Range" icon={CalendarRange}>
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="flex flex-wrap gap-2 max-w-2xl">
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...local, ...last12, timePeriod: 'last12Months' };
                      setLocal(next);
                      onApplyFilters(next);
                    }}
                    className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                      isLast12
                        ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                        : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                    }`}
                  >
                    Last 12 Months (Default)
                  </button>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="w-44">
                    <FilterField label="From Date" htmlFor="self-date-from">
                      <Input
                        id="self-date-from"
                        type="date"
                        value={local.dateFrom}
                        onChange={(e) => setLocal({ ...local, dateFrom: e.target.value, timePeriod: 'custom' })}
                      />
                    </FilterField>
                  </div>
                  <div className="w-44">
                    <FilterField label="To Date" htmlFor="self-date-to">
                      <Input
                        id="self-date-to"
                        type="date"
                        value={local.dateTo}
                        onChange={(e) => setLocal({ ...local, dateTo: e.target.value, timePeriod: 'custom' })}
                      />
                    </FilterField>
                  </div>
                </div>
              </div>
            </FilterSection>

            {/* Customer — what the dashboard's supplier view calls this group:
                the customers the supplier trades with. */}
            <SectionDivider />
            <FilterSection title="Customer" icon={Building2}>
              <FilterGrid>
                <FilterField label="Customer Group" htmlFor="self-customer-group">
                  <Select
                    value={local.division}
                    onValueChange={(value) => {
                      // Changing group drops a site that is no longer in it.
                      const next = { ...local, division: value, site: 'all' };
                      setLocal(next);
                      onApplyFilters(next);
                    }}
                  >
                    <SelectTrigger id="self-customer-group"><SelectValue placeholder="All Groups" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Groups</SelectItem>
                      {options.groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FilterField>
                <FilterField label="Customer Site" htmlFor="self-customer-site">
                  <Select value={local.site} onValueChange={(value) => applyNow('site', value)}>
                    <SelectTrigger id="self-customer-site"><SelectValue placeholder="All Sites" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sites</SelectItem>
                      {sitesInGroup.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FilterField>
              </FilterGrid>
            </FilterSection>

            {/* Supplier — only when the supplier has more than one of its own
                sites in the rows (GROUP view); a single site has nothing to pick. */}
            {options.supplierSites.length > 1 && (
              <>
                <SectionDivider />
                <FilterSection title="Supplier" icon={Truck}>
                <FilterGrid>
                  <FilterField label="Supplier Site" htmlFor="self-supplier-site">
                    <Select value={local.supplierSite} onValueChange={(value) => applyNow('supplierSite', value)}>
                      <SelectTrigger id="self-supplier-site"><SelectValue placeholder="All my sites" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All my sites</SelectItem>
                        {options.supplierSites.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FilterField>
                </FilterGrid>
                </FilterSection>
              </>
            )}

            <SectionDivider />
            <FilterSection title="Classification" icon={Tags}>
              <FilterGrid>
                <FilterField label="Rating" htmlFor="self-rating">
                  <Select
                    value={local.rating}
                    onValueChange={(value) => {
                      const next = value === 'N/A'
                        ? { ...local, rating: value, excludeNARatings: false }
                        : { ...local, rating: value };
                      setLocal(next);
                      onApplyFilters(next);
                    }}
                  >
                    <SelectTrigger id="self-rating"><SelectValue placeholder="All Ratings" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ratings</SelectItem>
                      {options.grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      <SelectItem value="N/A">N/A (No Data)</SelectItem>
                    </SelectContent>
                  </Select>
                </FilterField>
              </FilterGrid>
              <div className="flex items-center gap-2 mt-4 w-fit">
                <Checkbox
                  id="self-exclude-na"
                  checked={local.excludeNARatings}
                  onCheckedChange={(checked) => applyNow('excludeNARatings', checked === true)}
                />
                <Label htmlFor="self-exclude-na" className="text-sm font-medium cursor-pointer">Exclude N/A Ratings</Label>
              </div>
            </FilterSection>

            {/* Footer: Clear All left, status centre, Apply Filters right —
                the same arrangement as the customer-side panel and the dashboard. */}
            <div className="px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Button type="button" variant="outline" onClick={clearFilters} className="px-6">
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>

                {hasPendingChanges ? (
                  <p className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertCircle className="h-4 w-4" />
                    Your selection isn't in the results yet — click Apply Filters
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Showing results for {rangeSummary}</p>
                )}

                <Button
                  type="button"
                  onClick={() => onApplyFilters(local)}
                  className={`px-6 ${
                    hasPendingChanges ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-background' : ''
                  }`}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
